import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Optional, Output } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';

@Component({
    selector: 'viva-select',
    templateUrl: './viva-select.component.html',
    styleUrls: ['./viva-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VivaSelectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class VivaSelectComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() control: AbstractControl | null = null;
  @Input() formControlName: string | number | null = null;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() interfaceOptions?: any = {}; // Initialize with an empty object
  @Input() iconStartSrc?: string;
  @Input() iconEndSrc?: string;
  @Input() required?: string;
  @Input() autocomplete?: string;

  @Output() changeEvent = new EventEmitter<Event>();
  @Output() ionChange = new EventEmitter<any>();
  @Input() value: any = '';
  @Output() valueChange = new EventEmitter<any>();

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };
  private nativeElement: HTMLIonInputElement;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
    private _translator: TranslatorService
  ) {
    this.nativeElement = elRef.nativeElement as HTMLIonInputElement;
  }

  ngOnInit(): void {
    if (!this.control && this.formControlName !== null && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup;
      if (formGroup) {
        this.control = formGroup.get(String(this.formControlName)) as FormControl;
      }
    }
  }

  get okText() {
    return this._translator.translateText('viva-select-component.ok');
  }

  get cancelText() {
    return this._translator.translateText('viva-select-component.cancel');
  }

  ngAfterViewInit(): void {
    this.showHideNotch();
  }

  writeValue(value: any): void {
    this.value = value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.nativeElement.setAttribute('disabled', 'true');
    } else {
      this.nativeElement.removeAttribute('disabled');
    }
  }

  onInputChange(event: any): void {
    this.value = event.target.value;
    this.valueChange.emit(this.value);
    this.showHideNotch();
    this.onChange(this.value);
    this.changeEvent.emit(event);
    this.ionChange.emit(event);
  }

  onInputBlur(): void {
    this.onTouched();
  }

  get isInvalid(): boolean {
    return this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : false;
  }

  get labelText(): string | undefined {
    if (this.value == 0) {
      return this.label;
    }
    if (this.value == null || this.value == undefined || this.value == '') {
      return undefined;
    }
    return this.label;
  }

  get actualPlaceholder(): string | undefined {
    return this.placeholder || this.label;
  }

  showHideNotch(): void {
    const iconElement = this.nativeElement.querySelector('.input-outline-notch') as HTMLElement | null;
    if (iconElement) {
      if (this.value == 0) {
        iconElement.style.display = 'block';
      } else {
        if (this.value == null || this.value == undefined || this.value == '') {
          iconElement.style.display = 'none';
        } else {
          iconElement.style.display = 'block';
        }
      }
    } else {
      setTimeout(() => this.showHideNotch(), 500);
    }
  }
}
